import {
  Button,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper
} from '@mui/material';
import { ChevronRight, ExpandLess, ExpandMore } from '@mui/icons-material';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { PermissionBoundary } from '../../../../../../core/permissions/permission-boundary.component';
import { NavigationItem } from '../../navigation-bar.component';
import { NavigationMenuBoundary } from '../../navigation-menu-boundary.component';
import { collectNavigationUrls } from '../../navigation-item.utils';
import { useNavMenuCollapsibleDesktopItemStyles } from './nav-menu-collapsible-desktop-item.styles';

export function NavMenuCollapsibleDesktopItem({ item }: { item: NavigationItem }) {
  const match = useRouteMatch({ path: collectNavigationUrls(item.children), exact: true });
  const classes = useNavMenuCollapsibleDesktopItemStyles({ isSelected: match?.isExact });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const isExpanded = Boolean(anchorEl);
  const popperId = isExpanded ? 'nav-popper' : undefined;

  return (
    <NavigationMenuBoundary item={item}>
      <Button
        onClick={handleClick}
        className={classes.menuItemsDesktop__button}
        variant="contained"
        endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
      >
        {item.text}
      </Button>
      <Popper
        id={popperId}
        open={isExpanded}
        anchorEl={anchorEl}
        placement="bottom-start"
        className={classes.menuItemsDesktop__popper}
      >
        {/* A nested menu's flyout is portalled, but it is still inside this listener's React tree. */}
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper>
            <DesktopMenuEntries items={item.children} onNavigate={() => setAnchorEl(null)} />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </NavigationMenuBoundary>
  );
}

function DesktopMenuEntries({ items = [], onNavigate }: { items?: NavigationItem[]; onNavigate(): void }) {
  return (
    <List component="div" disablePadding>
      {items.map((child) =>
        Array.isArray(child.children) ? (
          <NestedDesktopMenuItem key={child.text} item={child} onNavigate={onNavigate} />
        ) : (
          <CollapsibleDesktopMenuItemChild key={child.text} child={child} onNavigate={onNavigate} />
        )
      )}
    </List>
  );
}

/**
 * A child that opens its own menu to the right. Hover opens it, as does a click for touch screens;
 * ArrowRight steps into it and ArrowLeft or Escape steps back out.
 */
function NestedDesktopMenuItem({ item, onNavigate }: { item: NavigationItem; onNavigate(): void }) {
  const match = useRouteMatch({ path: collectNavigationUrls(item.children), exact: true });
  const classes = useNavMenuCollapsibleDesktopItemStyles({ isSelected: match?.isExact });

  const itemRef = useRef<HTMLDivElement | null>(null);
  const flyoutRef = useRef<HTMLDivElement | null>(null);
  const shouldFocusFlyout = useRef(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen && shouldFocusFlyout.current) {
      shouldFocusFlyout.current = false;
      flyoutRef.current?.querySelector<HTMLElement>('[role="button"]')?.focus();
    }
  }, [isOpen]);

  const handleItemKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      shouldFocusFlyout.current = true;
      setIsOpen(true);
    }
  };

  const handleFlyoutKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'Escape') {
      e.stopPropagation();
      setIsOpen(false);
      itemRef.current?.focus();
    }
  };

  return (
    <NavigationMenuBoundary item={item}>
      <div onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
        <ListItemButton
          ref={itemRef}
          aria-haspopup="true"
          aria-expanded={isOpen}
          className={classes.menuItemsDesktop__childButton}
          onClick={() => setIsOpen(true)}
          onKeyDown={handleItemKeyDown}
        >
          {item.icon ? (
            <ListItemIcon className={classes.menuItemDesktop_IconWrapper}>
              <item.icon className={classes.menuItemDesktop_Icon} />
            </ListItemIcon>
          ) : null}

          <ListItemText primary={item.text} />
          <ChevronRight className={classes.menuItemDesktop__nestedChevron} />
        </ListItemButton>
        {/* Kept mounted so the flyout's items exist to take focus the moment it opens. */}
        <Popper
          open={isOpen}
          anchorEl={itemRef.current}
          placement="right-start"
          keepMounted
          className={classes.menuItemsDesktop__popper}
        >
          <Paper ref={flyoutRef} onKeyDown={handleFlyoutKeyDown}>
            <DesktopMenuEntries items={item.children} onNavigate={onNavigate} />
          </Paper>
        </Popper>
      </div>
    </NavigationMenuBoundary>
  );
}

function CollapsibleDesktopMenuItemChild({ child, onNavigate }: { child: NavigationItem; onNavigate(): void }) {
  const match = useRouteMatch({ path: child.url, exact: true });
  const classes = useNavMenuCollapsibleDesktopItemStyles({ isSelected: match?.isExact });
  return (
    <PermissionBoundary
      key={`${child.text}${child.url}`}
      permissions={child.permissions}
      availableUiFeatures={child.availableUiFeatures}
    >
      <ListItemButton
        key={child.text}
        className={classes.menuItemsDesktop__childButton}
        {...(child.url ? { component: Link, to: child.url } : {})}
        onClick={onNavigate}
      >
        {child.icon ? (
          <ListItemIcon className={classes.menuItemDesktop_IconWrapper}>
            <child.icon className={classes.menuItemDesktop_Icon} />
          </ListItemIcon>
        ) : null}

        <ListItemText primary={child.text} />
      </ListItemButton>
    </PermissionBoundary>
  );
}
